<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

import { useFocusTrap } from '@/shared/lib/composables';
import { useConfirm } from '@/shared/lib/confirm/use-confirm';

import AppButton from './button/AppButton.vue';
import BaseModal from './modal/BaseModal.vue';
import AppTextarea from './textarea/AppTextarea.vue';

const { canConfirm, inputValue, isOpen, options, handleConfirm, handleCancel } = useConfirm();
const actions = computed(() =>
  options.value?.primaryAction === 'cancel' ? (['confirm', 'cancel'] as const) : (['cancel', 'confirm'] as const),
);
const contentRef = useTemplateRef<HTMLElement>('content');

// BaseModal фокус не берёт: без своей ловушки Tab забрала бы ловушка редактора под диалогом.
useFocusTrap(
  () => contentRef.value,
  () => isOpen.value,
);
</script>

<template>
  <BaseModal
    :show="isOpen"
    size="sm"
    elevated
    @close="handleCancel"
  >
    <template #title>{{ options?.title }}</template>

    <div
      ref="content"
      tabindex="-1"
      class="outline-none"
    >
      <p class="text-(--base-text-color)">{{ options?.message }}</p>

      <label
        v-if="options?.input"
        class="mt-4 block text-sm font-medium text-(--base-text-color)"
      >
        {{ options.input.label }}
        <AppTextarea
          v-model="inputValue"
          class="mt-2"
          :placeholder="options.input.placeholder"
        />
      </label>

      <!-- Фокус на отмене: случайный Enter не подтверждает действие. У ввода причины фокус получает поле. -->
      <div class="mt-6 flex justify-end gap-3">
        <AppButton
          v-for="(action, index) in actions"
          :key="action"
          :outline="index === 0"
          :disabled="action === 'confirm' && !canConfirm"
          :data-autofocus="action === 'cancel' && !options?.input ? '' : undefined"
          @click="action === 'confirm' ? handleConfirm() : handleCancel()"
        >
          {{ action === 'confirm' ? options?.confirmText : options?.cancelText }}
        </AppButton>
      </div>
    </div>
  </BaseModal>
</template>
